import React, { useCallback, useEffect, useRef, useState } from 'react';

/** Hover must last this long before the label expands past the shared "Notes" width. */
export const LONG_HOVER_EXPAND_MS = 3000;

type NavTabIcon =
  | 'properties'
  | 'leases'
  | 'contacts'
  | 'expenses'
  | 'communications'
  | 'notes'
  | 'reports'
  | 'imports'
  | 'addresses'
  | 'categories';

interface AppNavTabProps {
  label: string;
  icon: NavTabIcon;
  isActive: boolean;
  isDirty?: boolean;
  onClick: () => void;
}

const iconProps = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true
};

const NavTabIcons: Record<NavTabIcon, React.ReactNode> = {
  properties: (
    <svg {...iconProps}>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V20h14V9.5" />
      <path d="M9 20v-6h6v6" />
    </svg>
  ),
  leases: (
    <svg {...iconProps}>
      <path d="M4 4h16v16H4z" />
      <path d="M8 8h8" />
      <path d="M8 12h8" />
      <path d="M8 16h5" />
    </svg>
  ),
  contacts: (
    <svg {...iconProps}>
      <circle cx="9" cy="8" r="3" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
      <path d="M14.5 20c0-2.2 1.5-4 3.5-4.5" />
    </svg>
  ),
  expenses: (
    <svg {...iconProps}>
      <path d="M12 2v20" />
      <path d="M16 6H9.5a2.5 2.5 0 0 0 0 5H14a2.5 2.5 0 0 1 0 5H8" />
    </svg>
  ),
  communications: (
    <svg {...iconProps}>
      <path d="M4 5h16v10H7l-3 3V5z" />
      <path d="M8 10h8" />
      <path d="M8 13h5" />
    </svg>
  ),
  notes: (
    <svg {...iconProps}>
      <path d="M12 19h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  ),
  reports: (
    <svg {...iconProps}>
      <path d="M4 20V4" />
      <path d="M4 20h16" />
      <path d="M8 16v-4" />
      <path d="M12 16V8" />
      <path d="M16 16v-6" />
    </svg>
  ),
  imports: (
    <svg {...iconProps}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  ),
  addresses: (
    <svg {...iconProps}>
      <path d="M12 21s7-5.2 7-11a7 7 0 1 0-14 0c0 5.8 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  ),
  categories: (
    <svg {...iconProps}>
      <path d="M4 6h16" />
      <path d="M4 12h10" />
      <path d="M4 18h14" />
      <circle cx="18" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="20" cy="18" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  )
};

const AppNavTab: React.FC<AppNavTabProps> = ({ label, icon, isActive, isDirty = false, onClick }) => {
  const [longHover, setLongHover] = useState(false);
  const longHoverTimerRef = useRef<number | null>(null);

  const clearLongHoverTimer = useCallback(() => {
    if (longHoverTimerRef.current !== null) {
      window.clearTimeout(longHoverTimerRef.current);
      longHoverTimerRef.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    clearLongHoverTimer();
    longHoverTimerRef.current = window.setTimeout(() => {
      setLongHover(true);
      longHoverTimerRef.current = null;
    }, LONG_HOVER_EXPAND_MS);
  }, [clearLongHoverTimer]);

  const handleMouseLeave = useCallback(() => {
    clearLongHoverTimer();
    setLongHover(false);
  }, [clearLongHoverTimer]);

  useEffect(() => () => clearLongHoverTimer(), [clearLongHoverTimer]);

  return (
    <button
      type="button"
      className={`app-nav-tab${isActive ? ' active' : ''}${isDirty ? ' dirty' : ''}${longHover ? ' long-hover' : ''}`}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      aria-label={isDirty ? `* ${label}` : label}
      aria-current={isActive ? 'page' : undefined}
    >
      {isDirty && <span className="app-nav-tab-dirty" aria-hidden>*</span>}
      <span className="app-nav-tab-icon">{NavTabIcons[icon]}</span>
      <span className="app-nav-tab-label">{label}</span>
    </button>
  );
};

export default AppNavTab;
